import React, { useEffect, useState } from 'react';
import { FaPhoneAlt, FaTimes } from 'react-icons/fa';
import { subscribeAgentReconnect } from '../agentReconnectStore';

/**
 * Auto-callback banner. At a callback's scheduled time the backend pauses
 * auto calling, sends {type:"callback", number, name, remarks, callbacktime}
 * and dials the number a few seconds later (the CRM opens like a manual
 * call). The banner blinks until the agent submits the disposition
 * ({type:"callback_done"}, auto calling resumes) or the dial could not be
 * placed ({type:"callback_failed", message}).
 */
const CallbackAutoBanner = () => {
  const [callback, setCallback] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    // After a page reload the SSE event is gone — ask whether one is running.
    if (localStorage.getItem('roles') === 'ROLE_USER') {
      fetch(process.env.REACT_APP_IP + '/User/CallbackAutoStatus', { credentials: 'include' })
        .then(r => (r.ok ? r.json() : null))
        .then(j => { if (j && j.active) setCallback(c => c || { number: '', name: '' }); })
        .catch(() => {});
    }
    const unsub = subscribeAgentReconnect((event) => {
      if (!event) return;
      if (event.type === 'callback') {
        setNotice(null);
        setCallback({
          number: event.number || '',
          name: event.name || '',
          remarks: event.remarks || '',
          callbacktime: event.callbacktime || '',
        });
      } else if (event.type === 'callback_done') {
        setCallback(null);
        setNotice({ ok: true, message: event.message || 'Callback finished — auto calling resumed' });
      } else if (event.type === 'callback_failed') {
        setCallback(null);
        setNotice({ ok: false, message: event.message || 'The callback could not be placed' });
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 8000);
    return () => clearTimeout(t);
  }, [notice]);

  if (!callback && !notice) return null;

  return (
    <>
      <style>{`
        .cbab-root {
          position: fixed; top: 56px; left: 50%; transform: translateX(-50%);
          z-index: 1201; max-width: 720px; width: calc(100vw - 32px);
          display: flex; align-items: center; gap: 14px; padding: 12px 16px;
          border-radius: var(--radius-md, 8px); box-shadow: var(--shadow-lg, 0 8px 24px rgba(0,0,0,.18));
          color: #fff;
        }
        .cbab-calling { background: #16a34a; animation: cbab-blink 1s ease-in-out infinite; }
        .cbab-ok { background: #16a34a; }
        .cbab-fail { background: #dc2626; }
        @keyframes cbab-blink {
          0%, 100% { background: #16a34a; box-shadow: 0 0 0 0 rgba(22,163,74,.6); }
          50% { background: #0f7a35; box-shadow: 0 0 0 8px rgba(22,163,74,0); }
        }
        .cbab-icon { display: flex; align-items: center; justify-content: center; width: 34px; height: 34px;
          border-radius: 50%; background: rgba(255,255,255,.2); flex-shrink: 0; }
        .cbab-calling .cbab-icon svg { animation: cbab-ring .6s ease-in-out infinite alternate; }
        @keyframes cbab-ring { from { transform: rotate(-15deg); } to { transform: rotate(15deg); } }
        .cbab-text { flex: 1; min-width: 0; }
        .cbab-title { font-weight: 700; font-size: var(--text-base, 15px); }
        .cbab-sub { font-size: var(--text-sm, 13px); opacity: .92; margin-top: 2px; overflow-wrap: anywhere; }
        .cbab-x { background: transparent; border: 0; color: #fff; opacity: .8; cursor: pointer; padding: 6px; }
        .cbab-x:hover { opacity: 1; }
        @media (max-width: 600px) { .cbab-root { top: 8px; gap: 8px; padding: 8px 10px; } }
      `}</style>
      {callback ? (
        <div className="cbab-root cbab-calling" role="alert" aria-live="assertive">
          <div className="cbab-icon"><FaPhoneAlt /></div>
          <div className="cbab-text">
            <div className="cbab-title">
              I am calling callback number {callback.number}{callback.name ? ` (${callback.name})` : ''}
            </div>
            <div className="cbab-sub">
              Auto calling is paused. Submit the disposition, or schedule the callback again, to resume auto calling.
              {callback.callbacktime ? ` Scheduled for ${callback.callbacktime}.` : ''}
              {callback.remarks ? ` Remarks: ${callback.remarks}` : ''}
            </div>
          </div>
        </div>
      ) : (
        <div className={`cbab-root ${notice.ok ? 'cbab-ok' : 'cbab-fail'}`} role="status">
          <div className="cbab-icon"><FaPhoneAlt /></div>
          <div className="cbab-text"><div className="cbab-title">{notice.message}</div></div>
          <button className="cbab-x" onClick={() => setNotice(null)} aria-label="Dismiss"><FaTimes size={12} /></button>
        </div>
      )}
    </>
  );
};

export default CallbackAutoBanner;
